import type { ReactNode } from "react";
import { Switch } from "@/components/ui/switch";
import type {
  DeliveryStat,
  MessagingChannelLine,
  NotificationCell,
  NotificationEventRow,
  ZbsReadiness,
} from "../../shared/api/notificationSettings";
import { isInheritedPlaceholder } from "./EventChannelTemplates";
import {
  failureLabel,
  totalsFor,
  verdictOf,
  type Verdict,
} from "./notificationHealth";

export const TABLE_CHANNELS = [
  { channel: "ZBS", label: "Zalo ZBS" },
  { channel: "SMS", label: "SMS" },
  { channel: "EMAIL", label: "Email" },
] as const;

/** Why a channel will not send, in a few words — shown in the cell's tooltip. */
const PROBLEM: Partial<Record<ZbsReadiness, string>> = {
  NO_TEMPLATE: "Chưa chọn mẫu",
  TEMPLATE_NOT_FOUND: "Mẫu không còn tồn tại",
  TEMPLATE_ARCHIVED: "Mẫu đã lưu trữ",
  TEMPLATE_NOT_APPROVED: "Mẫu chưa được duyệt",
  TEMPLATE_UNSUITABLE: "Mẫu không đúng mục đích sự kiện",
  PARAMS_MISSING: "Mẫu thiếu tham số",
  UNKNOWN: "Chưa kiểm tra được",
};

export type CellView =
  | { kind: "unused" }
  | { kind: "off" }
  /** #19667 — switched on here, but Addy Message cannot send on the channel at all. */
  | { kind: "notConnected"; problem: string | null }
  | { kind: "loading" }
  | { kind: "error" }
  | {
      kind: "template";
      code: string | null;
      /** The provider's name for the template (#19571), shown instead of the code when known. */
      name?: string | null;
      tone: "ok" | "warn" | "error" | "unknown";
      problem: string | null;
    };

/**
 * What one (event, channel) cell of the overview says (#19578): unused by the event, switched off,
 * or the clinic-wide template with its readiness — the same verdict the server gives, never a
 * second rule on the client.
 */
export function cellView(
  ev: NotificationEventRow,
  channel: string,
  cell: NotificationCell | undefined,
): CellView {
  if (!ev.allowedChannels?.includes(channel)) return { kind: "unused" };
  const r = ev.channelReadiness?.[channel];
  if (!r || r === "DISABLED" || r === "CHANNEL_OFF") return { kind: "off" };
  const placeholder = !ev.systemMessage && isInheritedPlaceholder(cell, ev);
  const code = placeholder ? null : (cell?.templateCode ?? null);
  const tone =
    r === "READY"
      ? "ok"
      : r === "TEMPLATE_UNSUITABLE"
        ? "error"
        : r === "UNKNOWN"
          ? "unknown"
          : "warn";
  return { kind: "template", code, tone, problem: PROBLEM[r] ?? null };
}

/**
 * #19667 (U1/U2) — a template verdict says nothing useful while the channel itself cannot send:
 * the SMS cell of the OTP used to show the Zalo template's name with "chưa được duyệt" when the
 * real reason was that SMS had no provider. The connection is the first question, so it wins.
 * "UNKNOWN" (Addy Message not asked) is left alone — never "not connected" for an unanswered
 * question.
 */
export function withConnection(
  view: CellView,
  line: MessagingChannelLine | undefined,
): CellView {
  if (view.kind !== "template" || line?.state !== "OFF") return view;
  return { kind: "notConnected", problem: line.problem ?? null };
}

const DOT: Record<
  "ok" | "warn" | "error" | "unknown",
  { mark: string; cls: string }
> = {
  ok: { mark: "●", cls: "text-emerald-600" },
  warn: { mark: "⚠", cls: "text-amber-600" },
  error: { mark: "⛔", cls: "text-red-600" },
  unknown: { mark: "○", cls: "text-muted-foreground" },
};

function ChannelCell({ label, view }: { label: string; view: CellView }) {
  // The channel's name is repeated inside the cell below `lg`, where there is no header row.
  const prefix = (
    <span className="text-muted-foreground lg:hidden">{label}: </span>
  );
  if (view.kind === "unused")
    return <span className="text-sm text-muted-foreground">{prefix}—</span>;
  if (view.kind === "loading" || view.kind === "error")
    return (
      <span
        className="text-sm text-muted-foreground"
        data-testid="channel-cell"
      >
        {prefix}
        {view.kind === "loading" ? "Đang tải…" : "Không tải được"}
      </span>
    );
  if (view.kind === "off")
    return <span className="text-sm text-muted-foreground">{prefix}tắt</span>;
  if (view.kind === "notConnected")
    return (
      <span
        className="flex min-w-0 items-center gap-1.5 text-sm text-amber-700"
        title={view.problem ?? undefined}
        data-testid="channel-cell"
      >
        {prefix}
        <span aria-hidden>⚠</span>
        <span className="truncate">Chưa kết nối</span>
      </span>
    );
  const dot = DOT[view.tone];
  const text = view.name ?? view.code ?? view.problem ?? "Chưa chọn mẫu";
  return (
    <span
      className="flex min-w-0 items-center gap-1.5 text-sm"
      title={
        [view.name, view.code, view.problem].filter(Boolean).join(" — ") ||
        undefined
      }
      data-testid="channel-cell"
    >
      {prefix}
      <span className={dot.cls} aria-hidden>
        {dot.mark}
      </span>
      <span
        className={
          "truncate " +
          (view.name ? "" : view.code ? "font-mono text-xs" : dot.cls)
        }
      >
        {text}
      </span>
    </span>
  );
}

export type Filter = "all" | "broken" | "attention" | "off";

const FILTERS: { key: Filter; icon: string; label: string }[] = [
  { key: "all", icon: "", label: "Tất cả" },
  { key: "broken", icon: "🔴", label: "Lỗi" },
  { key: "attention", icon: "🟠", label: "Cần xem" },
  { key: "off", icon: "", label: "Đang tắt" },
];

/** The URL spelling of a filter (`?loc=loi`), so a filtered view can be shared. */
export const FILTER_PARAM: Record<Filter, string | null> = {
  all: null,
  broken: "loi",
  attention: "can-xem",
  off: "tat",
};

export function filterFromParam(v: string | null): Filter {
  const hit = (Object.keys(FILTER_PARAM) as Filter[]).find(
    (k) => FILTER_PARAM[k] === v,
  );
  return hit ?? "all";
}

export function matchesFilter(f: Filter, v: Verdict): boolean {
  return f === "all" || f === v;
}

/**
 * The verdict chips that replace the old sentence-long summary (#19578): each one a count and a
 * filter. With nothing wrong, one short green line says so.
 */
export function FilterChips({
  verdicts,
  value,
  onChange,
}: {
  verdicts: Verdict[];
  value: Filter;
  onChange: (f: Filter) => void;
}) {
  const count = (f: Filter) =>
    verdicts.filter((v) => matchesFilter(f, v)).length;
  const healthy = count("broken") === 0 && count("attention") === 0;
  return (
    <div
      className="flex flex-wrap items-center gap-2"
      data-testid="filter-chips"
    >
      {FILTERS.map((f) => (
        <button
          key={f.key}
          type="button"
          aria-pressed={value === f.key}
          onClick={() => onChange(value === f.key ? "all" : f.key)}
          className={
            "rounded-full border px-3 py-1 text-xs " +
            (value === f.key
              ? "border-primary bg-primary/10 font-medium text-foreground"
              : "text-muted-foreground hover:bg-muted")
          }
        >
          {f.icon && <span aria-hidden>{f.icon} </span>}
          {f.label} {count(f.key)}
        </button>
      ))}
      {healthy && (
        <span className="text-xs text-emerald-700">
          🟢 Tất cả thông báo đang bật đều sẵn sàng gửi.
        </span>
      )}
    </div>
  );
}

const GRID =
  "grid gap-x-4 gap-y-1 lg:grid-cols-[minmax(220px,1.3fr)_minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(120px,0.8fr)_24px] lg:items-center";

/**
 * The overview of patient events (#19578): one row per event, one column per channel, the last
 * week's outcome, and a chevron. A row opens its detail below it; the switch does not open it.
 *
 * <p>Laid out as a grid rather than a <table> so the same rows become cards below `lg` without a
 * second rendering of the list.
 */
export function EventTable({
  events,
  cellsFor,
  stats,
  expanded,
  onToggleExpand,
  onToggleEnabled,
  savingEnabled,
  renderDetail,
  nameOf,
  connection,
}: {
  /** #19667 — Addy Message's per-channel connection; absent while unknown. */
  connection?: MessagingChannelLine[];
  /** #19571 — the provider's name for a template code on a channel; null when unknown. */
  nameOf?: (code: string, channel: string) => string | null;
  events: NotificationEventRow[];
  /** undefined = loading, null = failed — the cell says so instead of "Chưa chọn mẫu". */
  cellsFor: (eventKey: string) => NotificationCell[] | null | undefined;
  stats: DeliveryStat[] | null;
  expanded: string | null;
  onToggleExpand: (eventKey: string) => void;
  onToggleEnabled: (ev: NotificationEventRow, enabled: boolean) => void;
  savingEnabled: boolean;
  renderDetail: (ev: NotificationEventRow) => ReactNode;
}) {
  return (
    <div className="grid" data-testid="event-table">
      <div
        className={
          GRID +
          " hidden border-b pb-2 text-xs font-medium text-muted-foreground lg:grid"
        }
        aria-hidden
      >
        <span>Sự kiện</span>
        {TABLE_CHANNELS.map((c) => (
          <span key={c.channel}>{c.label}</span>
        ))}
        <span>7 ngày qua</span>
        <span />
      </div>
      {events.length === 0 && (
        <p className="py-4 text-sm text-muted-foreground">
          Không có thông báo nào trong nhóm này.
        </p>
      )}
      {events.map((ev) => {
        const open = expanded === ev.eventKey;
        const cells = cellsFor(ev.eventKey);
        const totals = stats ? totalsFor(ev.eventKey, stats) : null;
        const detailId = `event-detail-${ev.eventKey}`;
        return (
          <div
            key={ev.eventKey}
            id={`event-${ev.eventKey}`}
            className="scroll-mt-4 border-b last:border-b-0"
          >
            {/* Not role="button": the switch inside it is a control of its own, and an
                interactive element nested in another is read as one blurred control (review of
                #19576). The event's name is the button; a click anywhere else on the row is a
                mouse shortcut to it. */}
            <div
              data-testid={`event-row-${ev.eventKey}`}
              data-open={open || undefined}
              onClick={() => onToggleExpand(ev.eventKey)}
              className={
                GRID +
                " cursor-pointer rounded-md px-2 py-2.5 hover:bg-muted/50" +
                (open ? " bg-muted/40" : "")
              }
            >
              <span className="flex min-w-0 items-center gap-2.5">
                <span onClick={(e) => e.stopPropagation()}>
                  <Switch
                    checked={ev.enabled}
                    disabled={savingEnabled}
                    aria-label={`Bật ${ev.label}`}
                    onCheckedChange={(v) => onToggleEnabled(ev, v)}
                  />
                </span>
                <button
                  type="button"
                  aria-expanded={open}
                  aria-controls={open ? detailId : undefined}
                  title={ev.label}
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleExpand(ev.eventKey);
                  }}
                  className="min-w-0 truncate rounded text-left text-sm font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
                >
                  {ev.label}
                </button>
                {ev.eventKey === "PATIENT_OTP" && (
                  <span
                    className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground"
                    title="Dùng cho tra cứu kết quả khám trên website"
                  >
                    Website
                  </span>
                )}
              </span>
              {TABLE_CHANNELS.map((c) => (
                <ChannelCell
                  key={c.channel}
                  label={c.label}
                  view={
                    ev.enabled
                      ? withConnection(
                          cellsAware(
                          named(
                            cellView(
                              ev,
                              c.channel,
                              cells?.find(
                                (x) =>
                                  x.serviceId == null &&
                                  x.channel === c.channel,
                              ),
                            ),
                            c.channel,
                            nameOf,
                          ),
                          cells,
                        ),
                          connection?.find((l) => l.channel === c.channel),
                        )
                      : ev.allowedChannels?.includes(c.channel)
                        ? { kind: "off" }
                        : { kind: "unused" }
                  }
                />
              ))}
              <span
                className={
                  "text-sm " +
                  (totals && (totals.failed > 0 || totals.notSent > 0)
                    ? "text-amber-800"
                    : "text-muted-foreground")
                }
                title={
                  totals?.failures
                    .map((f) => `${failureLabel(f.code, f.channel)}: ${f.count}`)
                    .join("\n") || undefined
                }
                data-testid="row-stats"
              >
                <span className="text-muted-foreground lg:hidden">
                  7 ngày qua:{" "}
                </span>
                {totals
                  ? `${totals.ok} đã gửi` +
                    (totals.queued > 0 ? ` · ${totals.queued} chờ` : "") +
                    (totals.failed + totals.notSent > 0
                      ? ` · ${totals.failed + totals.notSent} lỗi`
                      : "")
                  : "—"}
              </span>
              <span
                className="hidden text-muted-foreground lg:block"
                aria-hidden
              >
                {open ? "▾" : "▸"}
              </span>
            </div>
            {open && (
              <div id={detailId} className="px-2 pb-4 pt-2">
                {renderDetail(ev)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** The provider's name on a template cell, when the catalogue has one (#19571). */
function named(
  view: CellView,
  channel: string,
  nameOf?: (code: string, channel: string) => string | null,
): CellView {
  if (view.kind !== "template" || !view.code || !nameOf) return view;
  return { ...view, name: nameOf(view.code, channel) };
}

/** A template cell whose cells have not arrived (or failed) says so, rather than "no template". */
function cellsAware(
  view: CellView,
  cells: NotificationCell[] | null | undefined,
): CellView {
  if (view.kind !== "template") return view;
  if (cells === undefined) return { kind: "loading" };
  if (cells === null) return { kind: "error" };
  return view;
}

/** Verdict per event, for the chips — the same function the summary used. */
export function verdictsFor(
  events: NotificationEventRow[],
  stats: DeliveryStat[] | null,
): Map<string, Verdict> {
  return new Map(
    events.map((ev) => [
      ev.eventKey,
      verdictOf(ev, stats ? totalsFor(ev.eventKey, stats) : null),
    ]),
  );
}
